import getFocusableElements from './get-focusable-elements';
import { isKey, keyMap } from '../keyboard-event';

export class NavigationContextManager {
  constructor() {
    this.contextStack = [];
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  updateContext(options = {}) {
    const currentContext = this.getCurrentContext();

    if (!currentContext) {
      return;
    }

    const focusableElements = getFocusableElements(currentContext.container);
    currentContext.focusableElements = focusableElements;

    if (!options.skipFocus && focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  pushContext(container, onClose = () => {}, options = {}) {
    const focusableElements = getFocusableElements(container);

    this.contextStack.push({
      container,
      focusableElements,
      onClose,
      prevFocusedElement: options.savePrevFocus && document.activeElement,
    });

    if (!options.skipFocus && focusableElements.length > 0) {
      focusableElements[0].focus();
    }

    if (this.contextStack.length === 1) {
      document.addEventListener('keydown', this.handleKeyDown);
    }
  }

  popContext() {
    const prevFocusedElement = this.getCurrentContext().prevFocusedElement;

    if (this.contextStack.length <= 1) {
      this.destroyContexts();

      if (prevFocusedElement) {
        prevFocusedElement.focus();
      }

      return;
    }

    this.contextStack.pop();

    if (prevFocusedElement) {
      prevFocusedElement.focus();
      return;
    }

    const prevContext = this.getCurrentContext();

    if (prevContext.focusableElements.length > 0) {
      prevContext.focusableElements[0].focus();
    }
  }

  getCurrentContext() {
    if (this.contextStack.length === 0) {
      return null;
    }

    return this.contextStack.at(-1);
  }

  handleKeyDown(event) {
    const context = this.getCurrentContext();

    if (!context) {
      return;
    }

    if (isKey(keyMap.ESCAPE, event) && this.contextStack.length > 0) {
      this.onKeyEscape(event);
    }

    if (isKey(keyMap.TAB, event)) {
      this.onKeyTab(event, context);
    }
  }

  onKeyEscape(event) {
    event.preventDefault();
    event.stopPropagation();

    const currentContext = this.getCurrentContext();
    currentContext.onClose();
  }

  onKeyTab(event, context) {
    const focusableElements = context.focusableElements;
    if (focusableElements.length === 0) {
      event.preventDefault();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements.at(-1);

    const isFirstElementFocused = document.activeElement === firstElement;
    const isLastElementFocused = document.activeElement === lastElement;
    const isBodyFocused = document.activeElement === document.body;

    if (event.shiftKey && (isFirstElementFocused || isBodyFocused)) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && (isLastElementFocused || isBodyFocused)) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  destroyContexts() {
    this.contextStack = [];
    document.removeEventListener('keydown', this.handleKeyDown);
  }
}

export default new NavigationContextManager();
